import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { SectionHead } from '@/components/layout';
import { Button, Container, Pill, Text } from '@/components/ui';
import { CourseRow } from '@/components/marketing';
import { SeoHead } from '@/components/SeoHead';
import {
  useCourseSections,
  usePrices,
  usePrivatstunden,
  type Price,
} from '@/content/courses';
import { useHover } from '@/lib/useHover';
import { MoodSection, fluidFontSize, fluidLineHeight, palette, useTheme } from '@/theme';

const webTransition = (props = 'background-color, color, transform, border-color', dur = '180ms') =>
  Platform.OS === 'web' ? ({ transitionProperty: props, transitionDuration: dur } as any) : null;

type LernpfadStep = { ix: string; lvl: string; title: string; body: string; highlight?: boolean };

const LERNPFAD_HIGHLIGHT_INDEX = 1;

export default function KurseScreen() {
  const theme = useTheme();
  const { t } = useTranslation();
  const courseSections = useCourseSections();
  const prices = usePrices();
  const privatstunden = usePrivatstunden();
  const lernpfadSteps = (
    t('kurse.lernpfad.steps', { returnObjects: true }) as LernpfadStep[]
  ).map((s, i) => (i === LERNPFAD_HIGHLIGHT_INDEX ? { ...s, highlight: true } : s));
  const infobarItems = t('kurse.preise.infobar', { returnObjects: true }) as Array<{
    label: string;
    value: string;
  }>;

  return (
    <View>
      <SeoHead
        title={t('kurse.seo.title')}
        description={t('kurse.seo.description')}
      />

      {/* ─── Pagehead ─── */}
      <View style={{ paddingTop: 48, paddingBottom: 32 }}>
        <Container>
          <View style={pgStyles.eyebrowRow}>
            <View style={[pgStyles.dot, { backgroundColor: theme.colors.accentAlt }]} />
            <Text variant="meta" tone="fgMute">
              {t('kurse.hero.eyebrow')}
            </Text>
          </View>
          <Text
            style={{
              fontFamily: theme.fontFamily.display,
              fontSize: fluidFontSize(48, 11.5, 96) as any,
              lineHeight: fluidLineHeight(0.95, 92) as any,
              color: theme.colors.fg,
              letterSpacing: -2,
            }}
          >
            {t('kurse.hero.titleLine1')}{'\n'}
            <Text
              italic
              style={{
                fontFamily: theme.fontFamily.displayItalic,
                fontStyle: 'italic',
                color: theme.colors.accent,
                fontSize: fluidFontSize(48, 11.5, 96) as any,
              }}
            >
              {t('kurse.hero.titleLine2')}
            </Text>
          </Text>
          <Text variant="bodyLarge" tone="fgSoft" style={{ marginTop: 24, maxWidth: 720 }}>
            {t('kurse.hero.intro')}
          </Text>
        </Container>
      </View>

      {/* ─── 01 Lernpfad ─── */}
      <View style={{ paddingVertical: 64 }}>
        <Container>
          <SectionHead
            idx={t('kurse.lernpfad.idx')}
            title={t('kurse.lernpfad.title')}
            sub={t('kurse.lernpfad.sub')}
          />
          <View style={pgStyles.stepper}>
            {lernpfadSteps.map((s) => (
              <StepCard key={s.title + s.ix} step={s} />
            ))}
          </View>
        </Container>
      </View>

      {/* ─── 02..n Course sections ─── */}
      {courseSections.map((section, i) => (
        <View key={section.id} style={{ paddingVertical: 64 }}>
          <Container>
            <SectionHead
              idx={`0${i + 2} / ${section.style}`}
              title={section.style.split(' ')[0]}
              italic={section.style.split(' ').slice(1).join(' ') || '·'}
              sub={section.intro}
            />
            <View style={pgStyles.stack}>
              {section.trial ? <CourseRow module={section.trial} accent={section.accent} /> : null}
              {section.modules.map((m) => (
                <CourseRow key={m.code + m.title} module={m} accent={section.accent} />
              ))}
            </View>
          </Container>
        </View>
      ))}

      {/* ─── Privatstunden (Midnight island) ─── */}
      <MoodSection
        mood="midnight"
        style={[
          pgStyles.midnight,
          Platform.OS === 'web'
            ? ({ boxShadow: `0 0 80px -20px ${palette.wine}55, 0 24px 60px -24px #15082E80` } as any)
            : null,
        ]}
      >
        <Container>
          <SectionHead
            idx={t('kurse.privatstunden.idx')}
            title={t('kurse.privatstunden.title')}
            italic={t('kurse.privatstunden.italic')}
            sub={privatstunden.body}
            invert
          />
          <View style={pgStyles.privatRow}>
            <View style={{ flex: 1, minWidth: 320 }}>
              <Button variant="ember" size="lg" trailingArrow href="/kontakt">
                {t('kurse.privatstunden.cta')}
              </Button>
            </View>
            <View style={pgStyles.rates}>
              {privatstunden.rates.map((r, i) => (
                <View
                  key={r.label}
                  style={[
                    pgStyles.rateRow,
                    { borderTopColor: 'rgba(245,240,255,0.16)' },
                    i === 0 && { borderTopWidth: 1 },
                  ]}
                >
                  <View style={{ flex: 1 }}>
                    <Text variant="eyebrow" style={{ color: palette.emberSoft }}>
                      {r.label}
                    </Text>
                    {r.note ? (
                      <Text variant="meta" style={{ color: 'rgba(245,240,255,0.6)', marginTop: 4 }}>
                        {r.note}
                      </Text>
                    ) : null}
                  </View>
                  <Text
                    italic
                    style={{
                      fontFamily: theme.fontFamily.displayItalic,
                      fontStyle: 'italic',
                      fontSize: 32,
                      color: palette.paper,
                    }}
                  >
                    {r.value}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </Container>
      </MoodSection>

      {/* ─── 06 Preise ─── */}
      <View style={{ paddingVertical: 96 }}>
        <Container>
          <SectionHead
            idx={t('kurse.preise.idx')}
            title={t('kurse.preise.title')}
            italic={t('kurse.preise.italic')}
            sub={t('kurse.preise.sub')}
          />
          <View style={pgStyles.priceGrid}>
            {prices.map((p) => (
              <PriceCard key={p.label} p={p} />
            ))}
          </View>

          <View
            style={[
              pgStyles.infobar,
              { borderColor: theme.colors.rule, borderRadius: theme.radii.md, backgroundColor: theme.colors.bgSoft },
            ]}
          >
            {infobarItems.map((it) => (
              <View key={it.label} style={pgStyles.infoItem}>
                <Text variant="eyebrow" tone="fgMute">
                  {it.label}
                </Text>
                <Text variant="body" style={{ marginTop: 4 }}>
                  {it.value}
                </Text>
              </View>
            ))}
          </View>

          <View style={pgStyles.ctaRow}>
            <Button variant="primary" size="lg" trailingArrow href="/kontakt">
              {t('kurse.preise.ctaPrimary')}
            </Button>
            <Button variant="ghost" size="lg" href="/info">
              {t('kurse.preise.ctaSecondary')}
            </Button>
          </View>
        </Container>
      </View>
    </View>
  );
}

function StepCard({ step }: { step: LernpfadStep }) {
  const theme = useTheme();
  const { hovered, hoverProps } = useHover();
  const isHl = !!step.highlight;
  return (
    <Pressable
      {...(hoverProps as any)}
      style={[
        pgStyles.step,
        {
          backgroundColor: isHl ? theme.colors.accent : hovered ? theme.colors.bgSoft : theme.colors.bg,
          borderColor: isHl ? theme.colors.accent : theme.colors.rule,
          borderRadius: theme.radii.md,
        },
        webTransition(),
        hovered && !isHl ? { transform: [{ translateY: -3 }] } : null,
      ]}
    >
      <View style={pgStyles.stepHead}>
        <Text variant="meta" style={{ color: isHl ? 'rgba(245,240,255,0.78)' : theme.colors.fgMute }}>
          {step.ix}
        </Text>
        <Text
          variant="meta"
          style={{ color: isHl ? palette.emberSoft : theme.colors.accent }}
        >
          {step.lvl}
        </Text>
      </View>
      <Text
        style={{
          fontFamily: theme.fontFamily.display,
          fontSize: 26,
          lineHeight: 30,
          color: isHl ? palette.paper : theme.colors.fg,
          marginTop: 16,
        }}
      >
        {step.title}
      </Text>
      <Text
        style={{
          color: isHl ? 'rgba(245,240,255,0.85)' : theme.colors.fgSoft,
          fontSize: 14,
          lineHeight: 22,
          marginTop: 10,
        }}
      >
        {step.body}
      </Text>
    </Pressable>
  );
}

function PriceCard({ p }: { p: Price }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hovered, hoverProps } = useHover();
  const isHl = !!p.highlighted;
  return (
    <Pressable
      {...(hoverProps as any)}
      style={[
        pgStyles.priceCard,
        {
          backgroundColor: isHl ? theme.colors.accent : theme.colors.bgSoft,
          borderRadius: theme.radii.md,
          borderColor: hovered && !isHl ? theme.colors.accent : theme.colors.rule,
        },
        webTransition(),
        hovered ? { transform: [{ translateY: -2 }] } : null,
      ]}
    >
      <Text
        variant="eyebrow"
        style={{ color: isHl ? 'rgba(245,240,255,0.78)' : theme.colors.fgMute }}
      >
        {p.label}
      </Text>
      <Text
        italic
        style={{
          color: isHl ? palette.paper : theme.colors.fg,
          fontFamily: theme.fontFamily.displayItalic,
          fontStyle: 'italic',
          fontSize: 56,
          lineHeight: 56,
          marginTop: 12,
        }}
      >
        {p.value}
      </Text>
      <Text
        variant="body"
        style={{
          color: isHl ? 'rgba(245,240,255,0.78)' : theme.colors.fgSoft,
          marginTop: 12,
        }}
      >
        {p.note}
      </Text>
      {isHl ? (
        <View style={{ marginTop: 16, alignSelf: 'flex-start' }}>
          <Pill variant="ember">{t('kurse.preise.bestsellerBadge')}</Pill>
        </View>
      ) : null}
    </Pressable>
  );
}

const pgStyles = StyleSheet.create({
  eyebrowRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 32,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  stepper: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  step: {
    flex: 1,
    minWidth: 180,
    padding: 20,
    borderWidth: 1,
  },
  stepHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  stack: { gap: 16 },
  midnight: {
    paddingVertical: 96,
    paddingHorizontal: 8,
    marginHorizontal: 24,
    marginVertical: 48,
    borderRadius: 12,
    overflow: 'hidden',
  },
  privatRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 48,
    alignItems: 'flex-start',
  },
  rates: { flex: 1, minWidth: 360 },
  rateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(245,240,255,0.16)',
    gap: 16,
  },
  priceGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  priceCard: { flex: 1, minWidth: 220, padding: 24, borderWidth: 1 },
  infobar: {
    marginTop: 32,
    padding: 24,
    borderWidth: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
  },
  infoItem: { flex: 1, minWidth: 200 },
  ctaRow: {
    marginTop: 32,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
});
